import { motion } from 'framer-motion';
import { Building2, TrendingUp, Users, Briefcase, Landmark, Wallet, BarChart3, Target, Zap, Award, Globe, Shield } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

interface FloatingCardProps {
  icon: React.ElementType;
  label: string;
  color: string;
  position: { top?: string; left?: string; right?: string; bottom?: string };
  delay: number;
  duration?: number;
}

const FloatingCard = ({ icon: Icon, label, color, position, delay, duration = 5 }: FloatingCardProps) => (
  <motion.div
    className="absolute"
    style={position}
    animate={{ y: [0, -10, 0], opacity: [0.5, 0.85, 0.5] }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
  >
    <div 
      className="w-16 h-14 bg-slate-800/50 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center gap-1 border"
      style={{ borderColor: `${color}20` }}
    >
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-[7px] text-slate-400 font-medium tracking-tight">{label}</span>
    </div>
  </motion.div>
);

export function AnimatedBackdrop() {
  const { isDarkMode } = useTheme();

  // Cards positioned in the side areas of the hero section (where the red circles were)
  const leftCards: FloatingCardProps[] = [
    { icon: Building2, label: 'Series A', color: '#3b82f6', position: { top: '15%', left: '8%' }, delay: 0 },
    { icon: Landmark, label: 'VC Firm', color: '#8b5cf6', position: { top: '32%', left: '12%' }, delay: 0.8 },
    { icon: Target, label: 'Strategic', color: '#ec4899', position: { top: '50%', left: '6%' }, delay: 1.6 },
    { icon: Shield, label: 'Corporate', color: '#06b6d4', position: { top: '68%', left: '10%' }, delay: 2.4 },
  ];

  const rightCards: FloatingCardProps[] = [
    { icon: TrendingUp, label: 'Seed Fund', color: '#22c55e', position: { top: '15%', right: '8%' }, delay: 0.5 },
    { icon: BarChart3, label: 'Growth', color: '#3b82f6', position: { top: '32%', right: '12%' }, delay: 1.3 },
    { icon: Users, label: 'Angel', color: '#f59e0b', position: { top: '50%', right: '6%' }, delay: 2.1 },
    { icon: Wallet, label: 'Family', color: '#ec4899', position: { top: '68%', right: '10%' }, delay: 2.9 },
  ];

  return (
    <div 
      className="fixed inset-0 -z-10"
      style={{
        background: isDarkMode
          ? 'linear-gradient(180deg, #0a0f1a 0%, #0f172a 30%, #1e293b 70%, #0f172a 100%)'
          : 'linear-gradient(180deg, #f8fbff 0%, #eff6ff 34%, #ffffff 72%, #f8fafc 100%)',
      }}
    >
      {/* Corner glows */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] opacity-15"
        style={{ background: isDarkMode ? 'radial-gradient(circle at 0% 0%, rgba(59, 130, 246, 0.5) 0%, transparent 70%)' : 'radial-gradient(circle at 0% 0%, rgba(59, 130, 246, 0.18) 0%, transparent 70%)' }} />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-15"
        style={{ background: isDarkMode ? 'radial-gradient(circle at 100% 0%, rgba(34, 211, 153, 0.4) 0%, transparent 70%)' : 'radial-gradient(circle at 100% 0%, rgba(34, 197, 94, 0.14) 0%, transparent 70%)' }} />

      {/* Left side cards - positioned in hero area */}
      {leftCards.map((card, i) => (
        <FloatingCard key={`left-${i}`} {...card} />
      ))}

      {/* Right side cards - positioned in hero area */}
      {rightCards.map((card, i) => (
        <FloatingCard key={`right-${i}`} {...card} />
      ))}

      {/* Center floating accent */}
      <motion.div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 opacity-20"
        animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <Zap className={isDarkMode ? 'w-8 h-8 text-blue-400' : 'w-8 h-8 text-blue-500'} />
      </motion.div>

      {/* Bottom gradient */}
      <div className={isDarkMode ? 'absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent' : 'absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-white via-white/80 to-transparent'} />
    </div>
  );
}

export function SimpleBackdrop() {
  const { isDarkMode } = useTheme();

  return (
    <div 
      className="fixed inset-0 -z-10"
      style={{
        background: isDarkMode
          ? 'linear-gradient(180deg, #0a0f1a 0%, #0f172a 100%)'
          : 'linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)',
      }}
    />
  );
}
